import { navigate, useRoute } from '../router/router';
import { parseRoute } from '../router/routes';
import { StorefrontRoute } from '../views/storefront/StorefrontRoute';
import { ChatRoute } from '../views/chat/ChatRoute';
import { ProfileRoute } from '../views/profile/ProfileRoute';
import { TasksRoute } from '../views/tasks/TasksRoute';
import { GiftsRoute } from '../views/gifts/GiftsRoute';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { bootOnce } from '../bootstrap';
import { IconStar } from '../ui/icons';
import { useI18n } from '../bootstrap/i18n';
import { OpenInTelegramOverlay } from '../views/external/OpenInTelegramOverlay';
import { TopupSheet, type TopupSheetContext, warmTopupPackagesCache } from '../views/topup/TopupSheet';
import { AgeCheckSheet } from '../views/verification/AgeCheckSheet';
import { PremiumSheet, type PremiumSheetContext } from '../views/premium/PremiumSheet';
import { QuestSheet, shouldAutoShowQuestSheet, markQuestShownToday } from '../views/quests/QuestSheet';
import { OnboardingSheet, useOnboardingState } from '../ui/sheets/OnboardingSheet';
import { getTelegramUser, type TelegramUser } from '../utils/telegramUser';
import { authedFetchJson, fetchJson, getAccessTokenOrNull } from '../utils/api';
import { trackEvent } from '../analytics';
import { PostCheckoutFeedbackSheet, type FeedbackContext } from '../ui/sheets/PostCheckoutFeedbackSheet';
import { decodeStartAppNavPath } from '../utils/startAppNavigation';

export function AppShell() {
  const route = useRoute();
  const services = bootOnce();
  const { t } = useI18n(services.i18n);

  // Check if we're inside Telegram WebApp (has valid initData)
  const isInsideTelegram = useMemo(() => {
    const tg = (window as any).Telegram?.WebApp;
    const initData = tg?.initData;
    return Boolean(initData && typeof initData === 'string' && initData.length > 0);
  }, []);

  const [openInTg, setOpenInTg] = useState(false);
  const [openInTgPath, setOpenInTgPath] = useState<string>('/');

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupCtx, setTopupCtx] = useState<TopupSheetContext>({ origin: 'storefront' });
  const [ageCheckOpen, setAgeCheckOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [premiumCtx, setPremiumCtx] = useState<PremiumSheetContext>({ origin: 'storefront' });
  const [questSheetOpen, setQuestSheetOpen] = useState(false);
  const [questSheetAutoShow, setQuestSheetAutoShow] = useState(false);
  const questPopupAttemptedRef = useRef(false);

  // Onboarding (Welcome Tutorial)
  const {
    showOnboarding, closeOnboarding, checked: onboardingChecked, slides: onboardingSlides,
    resumeSlide: onboardingResumeSlide, pauseOnboarding, resumeOnboarding, completeOnboarding,
    isPaused: onboardingPausedRef,
  } = useOnboardingState();

  const openTopup = (ctx: TopupSheetContext) => {
    setTopupCtx(ctx);
    setTopupOpen(true);
  };

  const openPremium = (ctx: PremiumSheetContext) => {
    setPremiumCtx(ctx);
    setPremiumOpen(true);
  };

  const handlePremiumClose = useCallback(() => {
    setPremiumOpen(false);
    if (onboardingPausedRef.current) {
      resumeOnboarding(3);
    }
  }, [onboardingPausedRef, resumeOnboarding]);

  const [tgUser, setTgUser] = useState<TelegramUser | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [isBillionaire, setIsBillionaire] = useState<boolean | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const subAttemptsRef = useRef<number>(0);
  const subInflightRef = useRef<Promise<void> | null>(null);
  const [premiumPolicy, setPremiumPolicy] = useState<{ premiumFirstEnabled: boolean; freeCreditsPurchaseEnabled: boolean } | null>(null);
  const [premiumBenefits, setPremiumBenefits] = useState<{
    free_messages: boolean;
    chat_messages_limit: number;
    chat_messages_period: 'day' | 'month';
    free_commands_count: number;
    commands_limit: number;
    commands_period: 'day' | 'month';
    overage_mode: 'use_credits' | 'block';
    hide_ad_models: boolean;
    hide_surprise_offers: boolean;
  } | null>(null);

  // Post-checkout feedback (global trigger)
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCtx, setFeedbackCtx] = useState<FeedbackContext>({});
  const feedbackTimerRef = useRef<any>(null);

  // Online users count (lifted from StorefrontRoute for header display)
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);

  // Support Telegram deep-links: t.me/<bot>?startapp=nav_<base64urlPath>
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    const raw = String(tg?.initDataUnsafe?.start_param || '').trim();
    const decoded = decodeStartAppNavPath(raw);
    if (!decoded) return;
    try {
      const url = new URL(decoded, window.location.origin);
      const r = parseRoute(url);
      if (r.name === 'not_found') return;
      navigate(r, { replace: true });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const apply = () => setTgUser(getTelegramUser());
    apply();
    const id = window.setInterval(apply, 1500);
    return () => window.clearInterval(id);
  }, []);

  // Warm topup packages cache ASAP (avoid paywall "Loading packages..." stage).
  useEffect(() => {
    void warmTopupPackagesCache(services);
  }, []);

  // Auto-show daily quests popup on app launch only (not on internal navigation)
  // Fetches data first to avoid flash when system is disabled
  useEffect(() => {
    // Only attempt once per app session
    if (questPopupAttemptedRef.current) return;
    if (route.name !== 'storefront') return;
    if (ageCheckOpen) return;
    if (showOnboarding) return;
    
    questPopupAttemptedRef.current = true;
    
    // Small delay to let the page settle
    const timer = setTimeout(async () => {
      if (!shouldAutoShowQuestSheet()) return;
      
      try {
        // Fetch quest data first to check if we should show popup
        const response = await authedFetchJson(services, '/api/quests/today');
        if (!response.ok) return;
        
        const data = response.data as { enabled: boolean; quest: { already_claimed?: boolean } | null };
        
        // Only show popup if system enabled AND has unclaimed quest
        if (data.enabled && data.quest && !data.quest.already_claimed) {
          markQuestShownToday();
          setQuestSheetAutoShow(true);
          setQuestSheetOpen(true);
        }
      } catch (err) {
        console.error('[AppShell] Failed to check quest status:', err);
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [route.name, ageCheckOpen, showOnboarding, services]);

  // If user identity changes (e.g., admin purged the user and they re-registered),
  // clear local-only gate and re-open age check.
  useEffect(() => {
    const onScopeChanged = () => {
      try { localStorage.removeItem('aicams_age_verified'); } catch {}
      setAgeCheckOpen(true);
    };
    window.addEventListener('aicams-user-scope-changed', onScopeChanged as any);
    return () => window.removeEventListener('aicams-user-scope-changed', onScopeChanged as any);
  }, []);

  // Age Gate Check
  useEffect(() => {
    // Fast path: if already verified locally, don't flash the sheet.
    const verified = localStorage.getItem('aicams_age_verified');
    if (verified === 'true') return;

    // Show initially (safe default)
    setAgeCheckOpen(true);

    // Verify against DB (background) to skip if already agreed on another device
    (async () => {
      const u = getTelegramUser();
      if (!u?.id) return;
      
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 5000 });
      if (!accessToken) return;

      const r = await fetchJson<{ ok: boolean; is_adult: boolean }>('/api/user/adult', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (r.ok && (r.data as any)?.is_adult === true) {
        localStorage.setItem('aicams_age_verified', 'true');
        setAgeCheckOpen(false);
      }
    })();
  }, []);

  const handleAgeConfirm = async () => {
    // 1. Local update (instant)
    localStorage.setItem('aicams_age_verified', 'true');
    setAgeCheckOpen(false);
    
    // 2. DB update (background)
    const u = getTelegramUser();
    if (u?.id) {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 5000 });
      if (accessToken) {
        await fetchJson('/api/user/adult', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ is_adult: true }),
        });
      }
    }
  };

  // Load credits - простая функция
  const loadCredits = useCallback(async () => {
    const telegramId = tgUser?.id || getTelegramUser()?.id;
    if (!telegramId) return;
    try {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) return;
      const r = await fetchJson<{ ok: boolean; credits_balance: number }>('/api/user/balance', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (r.ok) setCredits(Number((r.data as any)?.credits_balance ?? 0));
    } catch {}
  }, [services, tgUser?.id]);

  // Обновлять кредиты при ЛЮБОЙ навигации
  useEffect(() => {
    loadCredits();
    
    // Слушаем popstate напрямую для гарантии
    const onNav = () => {
      setTimeout(loadCredits, 100);
    };
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, [loadCredits]);

  // Also allow any feature (tasks/purchase/etc) to request an immediate credits refresh.
  useEffect(() => {
    const onReq = () => {
      void loadCredits();
    };
    window.addEventListener('aicams-credits-refresh', onReq as any);
    return () => window.removeEventListener('aicams-credits-refresh', onReq as any);
  }, [loadCredits]);

  // When user comes back from Telegram native flows, refresh balance best-effort.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void loadCredits();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadCredits]);

  // Post-checkout feedback: show modal when user returns from external checkout
  useEffect(() => {
    const onVisChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (feedbackOpen) return;

      // Check if there's a pending payment in localStorage (any provider)
      let pending: { provider: string; planId?: number; orderId?: string } | null = null;
      try {
        const cryptoStr = localStorage.getItem('pendingCryptoPayment');
        if (cryptoStr) {
          const c = JSON.parse(cryptoStr);
          if (c?.created_at && Date.now() - c.created_at < 10 * 60 * 1000) {
            pending = { provider: 'nowpayments', planId: c.plan_id, orderId: c.order_id };
          }
        }
      } catch {}
      if (!pending) {
        try {
          const cardStr = localStorage.getItem('pendingCardPayment');
          if (cardStr) {
            const c = JSON.parse(cardStr);
            if (c?.created_at && Date.now() - Number(c.created_at) < 10 * 60 * 1000) {
              pending = { provider: c.provider || 'centrobill', planId: c.plan_id, orderId: c.order_id || c.order_uuid };
            }
          }
        } catch {}
      }
      if (!pending) {
        try {
          const starsStr = localStorage.getItem('pendingStarsPayment');
          if (starsStr) {
            const c = JSON.parse(starsStr);
            if (c?.created_at && Date.now() - c.created_at < 5 * 60 * 1000) {
              pending = { provider: 'telegram', planId: c.plan_id };
            }
          }
        } catch {}
      }

      if (!pending) return;

      // Check "don't ask again" opt-out
      try {
        if (localStorage.getItem('checkoutFeedbackOptOut') === '1') return;
      } catch {}

      // Clear previous timer
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }

      // Fetch config and schedule modal
      const pendingCopy = { ...pending };
      fetchJson<{ ok: boolean; enabled: boolean; delay_sec: number; cooldown_hours: number }>('/api/checkout-feedback/config')
        .then((r) => {
          if (!r.ok) return;
          const d = r.data as any;
          if (!d?.enabled) return;

          const delaySec = Number(d.delay_sec) || 5;
          const cooldownHours = Number(d.cooldown_hours ?? 0);

          // Check cooldown with server value (0 = no cooldown, show every time)
          if (cooldownHours > 0) {
            try {
              const last = Number(localStorage.getItem('lastCheckoutFeedback') || '0');
              if (last > 0 && Date.now() - last < cooldownHours * 3600 * 1000) return;
            } catch {}
          }

          feedbackTimerRef.current = setTimeout(() => {
            // Re-check: is payment still pending? (polling may have resolved it)
            let stillPending = false;
            try {
              if (pendingCopy.provider === 'nowpayments' && localStorage.getItem('pendingCryptoPayment')) stillPending = true;
              if ((pendingCopy.provider === 'tribute' || pendingCopy.provider === 'centrobill') && localStorage.getItem('pendingCardPayment')) stillPending = true;
              if (pendingCopy.provider === 'telegram' && localStorage.getItem('pendingStarsPayment')) stillPending = true;
            } catch {}

            if (stillPending) {
              setFeedbackCtx(pendingCopy);
              setFeedbackOpen(true);
            }
          }, delaySec * 1000);
        })
        .catch(() => {});
    };

    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, [feedbackOpen]);

  // Post-checkout feedback: direct trigger from Stars (openInvoice callback, no visibilitychange)
  useEffect(() => {
    const onDirectFeedback = (e: Event) => {
      if (feedbackOpen) return;
      // Check "don't ask again" opt-out
      try {
        if (localStorage.getItem('checkoutFeedbackOptOut') === '1') return;
      } catch {}

      const detail = (e as CustomEvent).detail || {};
      const ctx: { provider: string; planId?: number; orderId?: string } = {
        provider: detail.provider || 'telegram',
        planId: detail.planId,
        orderId: detail.orderId,
      };

      // Show after short delay (2s)
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => {
        setFeedbackCtx(ctx);
        setFeedbackOpen(true);
      }, 2000);
    };

    window.addEventListener('aicams-checkout-feedback', onDirectFeedback);
    return () => {
      window.removeEventListener('aicams-checkout-feedback', onDirectFeedback);
    };
  }, [feedbackOpen]);

  // Load subscription status
  useEffect(() => {
    let cancelled = false;

    const refreshSubscription = async () => {
      if (subInflightRef.current) return subInflightRef.current;
      subInflightRef.current = (async () => {
        subAttemptsRef.current = 0;
        setIsPremium((prev) => (prev === null ? null : prev)); // keep prior until we have certainty

        while (!cancelled && subAttemptsRef.current < 3) {
          subAttemptsRef.current += 1;
          const token = await getAccessTokenOrNull(services, { timeoutMs: 8000 });
          if (!token) {
            // unknown (don’t downgrade) — retry
            await new Promise((r) => setTimeout(r, 1200));
            continue;
          }
          const r = await fetchJson<{ ok: boolean; subscription: { is_premium: boolean } }>('/api/user/subscription', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) {
            await new Promise((r2) => setTimeout(r2, 1200));
            continue;
          }
          const sub = (r.data as any)?.subscription;
          if (sub && typeof sub.is_premium === 'boolean') {
            setIsPremium(Boolean(sub.is_premium));
            setIsBillionaire(Boolean(sub.is_billionaire));
            // Billionaire takes priority
            const tier = sub.is_billionaire ? 'billionaire' : (sub.tier || 'free');
            setSubscriptionTier(tier);
            break;
          }
          await new Promise((r3) => setTimeout(r3, 1200));
        }
      })().finally(() => {
        subInflightRef.current = null;
      });
      return subInflightRef.current;
    };

    // Initial load
    void refreshSubscription();

    const onReq = () => {
      // Force refresh by clearing any in-flight request
      subInflightRef.current = null;
      void refreshSubscription();
    };
    window.addEventListener('aicams-subscription-refresh', onReq as any);

    const onVis = () => {
      if (document.visibilityState === 'visible') void refreshSubscription();
    };
    document.addEventListener('visibilitychange', onVis);

    const pollMs = services.config.env === 'test'
      ? (route.name === 'storefront' ? 4_000 : 15_000)
      : (route.name === 'storefront' ? 15_000 : 60_000);
    const pollId = window.setInterval(() => void refreshSubscription(), pollMs);

    return () => {
      cancelled = true;
      window.removeEventListener('aicams-subscription-refresh', onReq as any);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(pollId);
    };
  }, [services]);

  // Load premium monetization policy (admin-managed)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await fetchJson<{ ok: boolean; premiumFirstEnabled: boolean; freeCreditsPurchaseEnabled: boolean }>('/api/monetization/premium-policy', { cache: 'no-store' as any });
        if (!r.ok) return;
        const d: any = r.data;
        if (!d) return;
        if (cancelled) return;
        setPremiumPolicy({
          premiumFirstEnabled: typeof d.premiumFirstEnabled === 'boolean' ? d.premiumFirstEnabled : true,
          freeCreditsPurchaseEnabled: typeof d.freeCreditsPurchaseEnabled === 'boolean' ? d.freeCreditsPurchaseEnabled : false,
        });
      } catch {}
    };
    void run();
    const id = window.setInterval(run, services.config.env === 'test' ? 10_000 : 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [services]);

  // Load premium benefits config (admin-managed)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await fetchJson<{ ok: boolean; [key: string]: any }>('/api/premium/benefits');
        if (!r.ok) return;
        const d: any = r.data;
        if (!d) return;
        if (cancelled) return;
        setPremiumBenefits({
          free_messages: d.free_messages !== false,
          chat_messages_limit: Math.max(0, Math.trunc(Number(d.chat_messages_limit ?? 0) || 0)),
          chat_messages_period: (d.chat_messages_period === 'day' || d.chat_messages_period === 'month') ? d.chat_messages_period : 'month',
          free_commands_count: Math.max(0, Math.trunc(Number(d.free_commands_count ?? 0) || 0)),
          commands_limit: Math.max(0, Math.trunc(Number(d.commands_limit ?? d.free_commands_count ?? 0) || 0)),
          commands_period: (d.commands_period === 'day' || d.commands_period === 'month') ? d.commands_period : 'day',
          overage_mode: (d.overage_mode === 'block' || d.overage_mode === 'use_credits') ? d.overage_mode : 'use_credits',
          hide_ad_models: d.hide_ad_models === true,
          hide_surprise_offers: d.hide_surprise_offers === true,
        });
      } catch {}
    };
    void run();
    const id = window.setInterval(run, 120_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Load chat message cost (admin-managed)
  const [chatMessageCostCredits, setChatMessageCostCredits] = useState<number>(1);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await fetchJson<{ ok: boolean; message_cost_credits: number }>('/api/monetization/chat-message-cost', { cache: 'no-store' as any });
        if (!r.ok) return;
        const d: any = r.data;
        const n = Number(d?.message_cost_credits);
        if (cancelled) return;
        if (Number.isFinite(n) && n >= 0) setChatMessageCostCredits(Math.trunc(n));
      } catch {}
    };
    void run();
    const id = window.setInterval(run, services.config.env === 'test' ? 10_000 : 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [services]);

  // Refresh subscription on route changes (no app restart needed)
  useEffect(() => {
    try {
      window.dispatchEvent(new Event('aicams-subscription-refresh'));
    } catch {}
  }, [route.name]);

  // Allow child screens to open PremiumSheet globally.
  useEffect(() => {
    const onOpen = (ev: Event) => {
      try {
        const d = (ev as CustomEvent).detail || {};
        const ctx = d?.context as PremiumSheetContext;
        if (ctx) openPremium(ctx);
      } catch {}
    };
    window.addEventListener('aicams-open-premium-sheet', onOpen as any);
    return () => window.removeEventListener('aicams-open-premium-sheet', onOpen as any);
  }, []);

  // Allow child screens to open QuestSheet globally.
  useEffect(() => {
    const onOpen = () => {
      setQuestSheetAutoShow(false);
      setQuestSheetOpen(true);
    };
    window.addEventListener('aicams-open-quest-sheet', onOpen);
    return () => window.removeEventListener('aicams-open-quest-sheet', onOpen);
  }, []);

  // If JWT arrives slightly позднее, сделаем пару тихих ретраев, чтобы баланс не завис на '—'.
  useEffect(() => {
    if (credits !== null) return;
    if (!tgUser?.id) return;
    const t1 = window.setTimeout(() => { void loadCredits(); }, 1200);
    const t2 = window.setTimeout(() => { void loadCredits(); }, 3500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [credits, tgUser?.id, loadCredits]);

  // Global "Open in Telegram" overlay, triggered by auth-required actions.
  useEffect(() => {
    const onReq = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent).detail || {};
        const p = String(detail?.path || '').trim();
        if (p) setOpenInTgPath(p);
      } catch {}
      setOpenInTg(true);
    };
    window.addEventListener('aicams-auth-required', onReq as any);
    return () => window.removeEventListener('aicams-auth-required', onReq as any);
  }, []);

  const avatarLabel = useMemo(() => {
    const n = tgUser?.first_name || tgUser?.username || 'U';
    return n.slice(0, 1).toUpperCase();
  }, [tgUser]);

  const tasksFrom = route.name === 'tasks' ? route.from : undefined;

  const handleAppBack = useCallback(() => {
    // Sheets behave like sub-pages: Back should close them first.
    if (topupOpen) {
      // Request a smooth close (BottomSheet plays close animation then calls onClose).
      try {
        window.dispatchEvent(new Event('aicams-sheet-request-close'));
      } catch {
        setTopupOpen(false);
      }
      return;
    }
    // Chat: go back to storefront (instead of closing mini-app / random history)
    if (route.name === 'chat') {
      navigate({ name: 'storefront' });
      return;
    }
    // Tasks: go back deterministically (avoid Telegram/WebView history weirdness)
    if (route.name === 'tasks') {
      if (tasksFrom === 'profile') {
        navigate({ name: 'profile' }, { replace: true });
      } else {
        navigate({ name: 'storefront' }, { replace: true });
      }
      return;
    }
    // Profile: go back to storefront deterministically
    if (route.name === 'profile') {
      navigate({ name: 'storefront' }, { replace: true });
      return;
    }
    // Prefer browser history for anything else.
    try { history.back(); } catch {}
  }, [route.name, tasksFrom, topupOpen]);

  // Allow in-app back arrows to request the same behavior as Telegram BackButton.
  useEffect(() => {
    const onReq = () => handleAppBack();
    window.addEventListener('aicams-app-back', onReq);
    return () => window.removeEventListener('aicams-app-back', onReq);
  }, [handleAppBack]);

  // Telegram BackButton: show "Back" on internal screens (so Telegram doesn't show "Close").
  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    const back = tg?.BackButton;
    if (!back) return;

    // Also show Back when a sheet is open (otherwise Telegram shows "Close").
    const shouldShow = topupOpen || (route.name !== 'storefront' && route.name !== 'not_found');

    if (!shouldShow) {
      try { back.hide?.(); } catch {}
      return;
    }

    try { back.show?.(); } catch {}
    try { back.onClick?.(handleAppBack); } catch {}
    // IMPORTANT: Telegram WebApp BackButton.offClick requires the same handler reference.
    return () => {
      try { back.offClick?.(handleAppBack); } catch {}
    };
  }, [route.name, topupOpen, handleAppBack]);

  const creditsLabel = useMemo(() => {
    if (credits === null || Number.isNaN(credits)) return '—';
    // Shorten large numbers: 100K+
    if (credits >= 1_000_000) {
      return (credits / 1_000_000).toFixed(credits % 1_000_000 === 0 ? 0 : 1) + 'M';
    }
    if (credits >= 100_000) {
      return (credits / 1_000).toFixed(credits % 1_000 === 0 ? 0 : 0) + 'K';
    }
    return credits.toLocaleString();
  }, [credits]);

  // If opened outside Telegram, force show the "Open in Telegram" overlay
  // Block ALL other UI - only show the gate
  const showTelegramGate = !isInsideTelegram;

  // BLOCK: If outside Telegram, show ONLY the gate - nothing else
  if (showTelegramGate) {
    return (
      <OpenInTelegramOverlay
        desiredPath={openInTgPath}
        dismissable={false}
        onClose={() => {}}
      />
    );
  }

  // Chat is a full-screen experience with its own layout.
  // IMPORTANT: keep global overlays (e.g. "Open in Telegram") mounted even on chat route.
  if (route.name === 'chat') {
    return (
      <>
        {openInTg ? (
          <OpenInTelegramOverlay
            desiredPath={openInTgPath}
            dismissable={true}
            onClose={() => setOpenInTg(false)}
          />
        ) : null}
        {topupOpen ? (
          <TopupSheet
            open={topupOpen}
            context={topupCtx}
            onClose={() => setTopupOpen(false)}
          />
        ) : null}
        {premiumOpen ? (
          <PremiumSheet
            open={premiumOpen}
            context={premiumCtx}
            onClose={handlePremiumClose}
          />
        ) : null}
        {questSheetOpen ? (
          <QuestSheet
            open={questSheetOpen}
            autoShow={questSheetAutoShow}
            onClose={() => {
              setQuestSheetOpen(false);
              setQuestSheetAutoShow(false);
            }}
          />
        ) : null}
        
        {/* Age Verification (Blocking, Chat) */}
        <AgeCheckSheet 
          open={ageCheckOpen} 
          onConfirm={handleAgeConfirm} 
        />

        {/* Onboarding (Welcome Tutorial, Chat) */}
        {!ageCheckOpen && onboardingChecked && showOnboarding && (
          <OnboardingSheet
            open={showOnboarding}
            onClose={closeOnboarding}
            onOpenPremium={() => {
              pauseOnboarding();
              openPremium({ origin: 'onboarding' });
            }}
            slides={onboardingSlides}
            initialSlide={onboardingResumeSlide}
          />
        )}

        {/* Post-Checkout Feedback (global, Chat) */}
        <PostCheckoutFeedbackSheet
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          context={feedbackCtx}
        />

        <ChatRoute
          isPremium={isPremium}
          isBillionaire={isBillionaire}
          subscriptionTier={subscriptionTier}
          freeCreditsPurchaseEnabled={premiumPolicy?.freeCreditsPurchaseEnabled === true}
          messageCostCredits={chatMessageCostCredits}
          premiumBenefits={premiumBenefits}
          onOpenTopup={(ctx) => openTopup(ctx)}
          onOpenPremium={({ context }) => openPremium(context)}
        />
      </>
    );
  }

  return (
    <div class="appShell">
      {openInTg ? (
        <OpenInTelegramOverlay
          desiredPath={openInTgPath}
          dismissable={true}
          onClose={() => setOpenInTg(false)}
        />
      ) : null}
      {topupOpen ? (
        <TopupSheet
          open={topupOpen}
          context={topupCtx}
          onClose={() => setTopupOpen(false)}
        />
      ) : null}
      {premiumOpen ? (
        <PremiumSheet
          open={premiumOpen}
          context={premiumCtx}
          onClose={handlePremiumClose}
        />
      ) : null}
      {questSheetOpen ? (
        <QuestSheet
          open={questSheetOpen}
          autoShow={questSheetAutoShow}
          onClose={() => {
            setQuestSheetOpen(false);
            setQuestSheetAutoShow(false);
          }}
        />
      ) : null}

      {/* Age Verification (Blocking, Main) */}
      <AgeCheckSheet 
        open={ageCheckOpen} 
        onConfirm={handleAgeConfirm} 
      />

      {/* Onboarding (Welcome Tutorial, Main) */}
      {!ageCheckOpen && onboardingChecked && showOnboarding && (
        <OnboardingSheet
          open={showOnboarding}
          onClose={closeOnboarding}
          onOpenPremium={() => {
            pauseOnboarding();
            openPremium({ origin: 'onboarding' });
          }}
          slides={onboardingSlides}
          initialSlide={onboardingResumeSlide}
        />
      )}

      {/* Post-Checkout Feedback (global, Main) */}
      <PostCheckoutFeedbackSheet
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        context={feedbackCtx}
      />

      {route.name === 'storefront' && (
        <div class="storeHeader">
          <div class="storeHeaderRow">
            <button class="freeCreditsBtn" onClick={() => { trackEvent('free_credits_click', { origin: 'storefront' }); navigate({ name: 'tasks', from: 'storefront' }); }}>
              {t('btn.get_credits', 'Get free credits')}
            </button>

            <div class="storeLogoBlock">
              <div class="storeLogo" aria-label="AICAMS">
                AI<span>CAMS</span>
              </div>
              {onlineUsersCount > 0 && (
                <div class="storeOnline">
                  <span class="storeOnlineDot" />
                  online: {onlineUsersCount.toLocaleString()}
                </div>
              )}
            </div>

            <div class="storeHeaderRight">
              <button
                class="balanceBtn"
                onClick={() => {
                  const premiumFirst = premiumPolicy?.premiumFirstEnabled !== false;
                  const allowFreeCredits = premiumPolicy?.freeCreditsPurchaseEnabled === true;
                  if (premiumFirst && isPremium === false && !allowFreeCredits) {
                    openPremium({ origin: 'storefront' });
                    return;
                  }
                  openTopup({ origin: 'storefront' });
                }}
                title={t('btn.topup', 'Top Up Balance')}
              >
                <span class="balanceValue">{creditsLabel}</span>
                <IconStar class="starSvg" size={16} filled />
              </button>
              <div class="avatar-with-badge">
                <button class="avatarBtn" onClick={() => navigate({ name: 'profile' })} title={t('nav.profile', 'Profile')}>
                  {tgUser?.photo_url ? (
                    <img class="avatarImg" src={tgUser.photo_url} alt="" />
                  ) : (
                    <span class="avatarFallback">{avatarLabel}</span>
                  )}
                </button>
                {subscriptionTier !== null && (
                  <span class={`avatar-status-badge ${isBillionaire ? 'billionaire' : subscriptionTier === 'premium' ? 'premium' : 'free'}`}>
                    {isBillionaire ? '👑👑👑' : subscriptionTier === 'premium' ? t('user.premium', 'PREMIUM') : t('user.free', 'FREE')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div class={`content ${route.name === 'storefront' ? 'contentStorefront' : 'contentNoHeader'}`}>
        {route.name === 'storefront' && (
          <StorefrontRoute
            isPremium={isPremium}
            premiumBenefits={premiumBenefits}
            onOnlineUsersCount={setOnlineUsersCount}
            onOpenTopup={() => {
              const premiumFirst = premiumPolicy?.premiumFirstEnabled !== false;
              const allowFreeCredits = premiumPolicy?.freeCreditsPurchaseEnabled === true;
              if (premiumFirst && isPremium === false && !allowFreeCredits) {
                openPremium({ origin: 'storefront' });
                return;
              }
              openTopup({ origin: 'storefront' });
            }}
          />
        )}
        {route.name === 'profile' && (
          <ProfileRoute
            isPremium={isPremium}
            onOpenTopup={() => {
                const premiumFirst = premiumPolicy?.premiumFirstEnabled !== false;
                const allowFreeCredits = premiumPolicy?.freeCreditsPurchaseEnabled === true;
                if (premiumFirst && isPremium === false && !allowFreeCredits) {
                  openPremium({ origin: 'profile' });
                return;
              }
              openTopup({ origin: 'profile' });
            }}
          />
        )}
        {route.name === 'tasks' && <TasksRoute />}
        {route.name === 'gifts' && <GiftsRoute />}

      {route.name === 'not_found' && (
        <div class="card" style={{ padding: 12 }}>
          <div class="muted" style={{ fontSize: 12 }}>
            {t('error.route_not_implemented', 'Route not implemented yet')}
          </div>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(route, null, 2)}</pre>
        </div>
      )}
      </div>
    </div>
  );
}
